import { Check } from 'lucide-react';

export interface WizardStep {
  id: string;
  label: string;
}

interface WizardProgressProps {
  steps: WizardStep[];
  currentStep: number;
  onStepClick?: (step: number) => void;
  stepValidity?: boolean[];
  allowNavigation?: boolean;
}

export function WizardProgress({
  steps,
  currentStep,
  onStepClick,
  stepValidity = [],
  allowNavigation = true,
}: WizardProgressProps) {
  const handleStepClick = (index: number) => {
    if (!allowNavigation || !onStepClick) return;

    const isClickable = index <= currentStep || stepValidity.slice(0, index).every(Boolean);
    if (isClickable) {
      onStepClick(index);
    }
  };

  return (
    <div className="flex items-center justify-between">
      {steps.map((step, index) => {
        const isCompleted = index < currentStep && (stepValidity[index] ?? true);
        const isCurrent = index === currentStep;
        const isClickable = allowNavigation && onStepClick && (
          index <= currentStep || stepValidity.slice(0, index).every(Boolean)
        );

        return (
          <div key={step.id} className="flex items-center flex-1 last:flex-none">
            <button
              type="button"
              onClick={() => handleStepClick(index)}
              disabled={!isClickable}
              className={`flex flex-col items-center gap-2 transition-all ${
                isClickable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
              }`}
            >
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center border-2 font-medium transition-all ${
                  isCompleted
                    ? 'bg-emerald-500 border-emerald-500 text-white'
                    : isCurrent
                      ? 'bg-brand-600 border-brand-600 text-white'
                      : 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500'
                }`}
              >
                {isCompleted ? <Check className="w-5 h-5" /> : index + 1}
              </div>
              <span
                className={`text-xs font-medium whitespace-nowrap ${
                  isCurrent
                    ? 'text-brand-600 dark:text-brand-400'
                    : isCompleted
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {step.label}
              </span>
            </button>

            {index < steps.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-3 transition-colors ${
                  isCompleted ? 'bg-emerald-500' : 'bg-gray-200 dark:bg-gray-700'
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
